import { ReactNode, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'dashboard_secciones_colapsadas';

const leer = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') ?? {};
  } catch {
    return {};
  }
};

const guardar = (id: string, colapsada: boolean) => {
  try {
    const actual = leer();
    if (colapsada) actual[id] = true;
    else delete actual[id];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(actual));
  } catch {
    return;
  }
};

interface Props {
  id: string;
  titulo: string;
  children: ReactNode;
  forzarAbierta?: boolean;
  resumen?: ReactNode;
}

const DashboardSeccion = ({ id, titulo, children, forzarAbierta = false, resumen }: Props) => {
  const { t } = useTranslation();
  const [colapsada, setColapsada] = useState(() => !!leer()[id]);

  useEffect(() => {
    guardar(id, colapsada);
  }, [id, colapsada]);

  const abierta = forzarAbierta || !colapsada;

  return (
    <section className={`space-y-3 tour-seccion-${id}`}>
      <button
        type="button"
        onClick={() => setColapsada((c) => !c)}
        aria-expanded={abierta}
        title={abierta ? t('dashboard.sections.collapse') : t('dashboard.sections.expand')}
        className="group w-full flex items-center gap-3 text-left"
      >
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors whitespace-nowrap">
          {titulo}
        </h3>
        {!abierta && resumen && (
          <span className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
            {resumen}
          </span>
        )}
        <span className="flex-1 h-px bg-gray-200/80 dark:bg-white/10" />
        <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 group-hover:bg-gray-100 dark:group-hover:bg-white/10 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${abierta ? '' : '-rotate-90'}`}
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {abierta && (
          <motion.div
            key="contenido"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden -m-2 p-2"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default DashboardSeccion;
